import {Badge, Group, Tooltip, createStyles} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React from 'react';

interface ModelLabelsProps {
  capabilities: ReadonlyArray<string>;
  category: string;
}

export default function ModelLabels({capabilities, category}: ModelLabelsProps): JSX.Element {
  const {classes} = useStyles();
  const longestLabelLength = Math.max(
    category.length,
    ...capabilities.map((capability) => capability.length)
  );

  return (
    <Group aria-label="Model labels" className={classes.root} noWrap={true} role="group" spacing={6}>
      <ModelLabelBadge
        isCategory={true}
        label={category}
        truncateFirst={category.length === longestLabelLength}
      />
      {capabilities.map((capability) => (
        <ModelLabelBadge
          key={capability}
          label={capability}
          truncateFirst={capability.length === longestLabelLength}
        />
      ))}
    </Group>
  );
}

interface ModelLabelBadgeProps {
  isCategory?: boolean;
  label: string;
  truncateFirst: boolean;
}

function ModelLabelBadge({isCategory = false, label, truncateFirst}: ModelLabelBadgeProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useStyles();
  const tooltip = getModelLabelTooltip(label);
  const tooltipDescriptionId = useId();

  return (
    <>
      <Tooltip label={tooltip} multiline={true} openDelay={300} width={260} withArrow={true}>
        <Badge
          aria-describedby={tooltipDescriptionId}
          aria-label={label}
          className={cx(classes.badge, truncateFirst ? classes.truncateFirst : classes.preserveWidth)}
          classNames={{inner: isCategory ? classes.categoryLabel : classes.capabilityLabel}}
          color="gray"
          radius="sm"
          role="note"
          size="sm"
          variant="light"
        >
          {label}
        </Badge>
      </Tooltip>
      <span className={classes.visuallyHidden} id={tooltipDescriptionId}>
        {tooltip}
      </span>
    </>
  );
}

const MODEL_LABEL_TOOLTIPS: Record<string, string> = {
  molecules: 'Predicts structures containing small molecules',
  multimers: 'Predicts structures containing multiple protein chains',
  'single chain': 'Predicts the structure of one protein chain at a time',
  'structure prediction': 'Predicts 3D structures for proteins and biomolecular complexes',
};

function getModelLabelTooltip(label: string): string {
  return MODEL_LABEL_TOOLTIPS[label] ?? 'Describes a model capability';
}

const useStyles = createStyles((theme) => ({
  badge: {
    backgroundColor: theme.colors.gray[0],
    color: theme.colors.gray[7],
    height: 'auto',
    lineHeight: 1.2,
  },
  capabilityLabel: {
    fontWeight: 500,
    textTransform: 'lowercase',
  },
  categoryLabel: {
    fontWeight: 600,
    textTransform: 'lowercase',
  },
  // Anchors the visually hidden descriptions. Unanchored, their containing block is the nearest
  // positioned ancestor, which can sit outside the scroll region the labels render in: they then escape
  // its clipping and stretch that ancestor's scroll height, adding a second scrollbar.
  root: {
    position: 'relative',
  },
  preserveWidth: {
    flexShrink: 0,
    minWidth: 'max-content',
  },
  truncateFirst: {
    flexShrink: 1,
    minWidth: 0,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
}));
